import React from 'react';

export type DropdownValue = number | string;

export interface DropdownOption<V extends DropdownValue = DropdownValue> {
  value: V;
  label: string;
}

export interface DropdownProps<V extends DropdownValue = DropdownValue> {
  label?: string;
  value: V;
  onChange: (value: V) => void;
  options: Array<DropdownOption<V>>;
  placeholder?: string;
  placeholderValue?: V; // Defaults to '' for string or 0 for number when valueType is provided
  id?: string;
  name?: string;
  disabled?: boolean;
  // Rendering/styling hooks
  containerClassName?: string;
  selectClassName?: string;
  labelClassName?: string;
  // Controls how to coerce the select value type
  valueType?: 'number' | 'string';
}

function coerceValue(raw: string, valueType?: 'number' | 'string'): DropdownValue {
  if (valueType === 'number') {
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return raw;
}

const Dropdown = <V extends DropdownValue = DropdownValue>(props: DropdownProps<V>) => {
  const {
    label,
    value,
    onChange,
    options,
    placeholder,
    placeholderValue,
    id,
    name,
    disabled,
    containerClassName,
    selectClassName,
    labelClassName,
    valueType,
  } = props;

  const computedPlaceholderValue = ((): DropdownValue => {
    if (placeholderValue !== undefined) return placeholderValue;
    return valueType === 'number' ? 0 : '';
  })();

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = coerceValue(e.target.value, valueType) as V;
    onChange(newValue);
  };

  return (
    <div className={containerClassName}>
      {label && (
        <label htmlFor={id} className={labelClassName}>
          {label}
        </label>
      )}
      <select
        id={id}
        name={name}
        value={String(value)}
        onChange={handleChange}
        disabled={disabled}
        className={selectClassName}
      >
        {placeholder !== undefined && (
          <option value={String(computedPlaceholderValue)}>{placeholder}</option>
        )}
        {options.map((opt) => (
          <option key={String(opt.value)} value={String(opt.value)}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export default Dropdown;


